import { Address, FullCity } from "@deligoo/shared"
import { Button, CityField, fetchFindPostalCode, Field, Icon, Input, StreetField } from "@deligoo/ui"
import { debounce } from "lodash"
import { FormEvent, useEffect, useRef } from "react"
import NumberFormat from "react-number-format"
import { toast } from "react-toastify"

import { Card } from "@/components/Card"
import { ManualOrder } from "@/types"
import { handleStandardError } from "@/utils/errors"

import { useManualOrderFormContext } from ".."
import cls from "../AddManualOrderView.module.scss"

export const DeliveryDataView = () => {
  const { formState, setFormStateValue, setNextStep, setPreviousStep } = useManualOrderFormContext()

  const { company_name, phone, city, state, county, municipality, street, house_number, postal_code } =
    formState.values.delivery_attributes || {}

  function onInputChange(e: FormEvent<HTMLInputElement>) {
    setFormStateValue({
      delivery_attributes: {
        ...(formState.values.delivery_attributes || {}),
        [e.currentTarget.name]: e.currentTarget.value,
      },
    })
  }

  function submitStep() {
    let errorsCount = 0

    toast.dismiss("customerAddressError")

    if (
      !company_name ||
      !phone ||
      !city ||
      !street ||
      !house_number ||
      !postal_code ||
      !state ||
      !county ||
      !municipality
    ) {
      errorsCount++

      toast.error("Uzupełnij adres klienta", {
        toastId: "customerAddressError",
        updateId: "customerAddressError",
      })
    }

    if (!errorsCount) setNextStep()
  }

  async function updatePostalCode(deliveryAttributes: ManualOrder["delivery_attributes"]) {
    const { city, street, state, county, municipality, house_number } = deliveryAttributes

    const isValid = Boolean(city && street && state && county && municipality && house_number)

    if (!isValid || deliveryAttributes.postal_code) return

    const partialAddress = {
      city,
      street,
      state,
      county,
      municipality,
      house_number,
    } as Omit<Address, "postal_code">

    const { response, json } = await fetchFindPostalCode(partialAddress)

    if (json.errors) {
      handleStandardError(response, json)
      return
    }

    if (response.ok && json.data) {
      setFormStateValue({
        delivery_attributes: {
          ...deliveryAttributes,
          postal_code: json.data.postal_code,
        },
      })
    }
  }

  const debouncedUpdatePostalCode = useRef(debounce(updatePostalCode, 1000)).current

  useEffect(() => {
    const deliveryAttributes = formState.values.delivery_attributes

    if (deliveryAttributes) {
      debouncedUpdatePostalCode(deliveryAttributes)
    } else {
      debouncedUpdatePostalCode.cancel()
    }

    return () => {
      debouncedUpdatePostalCode.cancel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city, county, debouncedUpdatePostalCode, house_number, municipality, state, street])

  return (
    <Card className={cls.card}>
      <Card.Section>
        <h3>Wprowadź dane dostawy</h3>
      </Card.Section>

      <Card.Section className={cls.deliveryData}>
        <Field
          required
          label="Nazwa"
          name="company_name"
          inputProps={{
            value: company_name || "",
            onChange: onInputChange,
          }}
          className={cls.fullWidth}
        />

        <Field required label="Numer telefonu" name="phone">
          <NumberFormat
            format="### ### ###"
            customInput={Input}
            name="phone"
            id="phone"
            value={phone || ""}
            onChange={onInputChange}
            size="large"
            plain
            selectOnFocus
          />
        </Field>

        <CityField
          value={city || ""}
          setValue={(value: FullCity) => {
            setFormStateValue({
              delivery_attributes: {
                ...(formState.values.delivery_attributes || {}),
                postal_code: undefined,
                ...value,
              },
            })
          }}
        />

        <StreetField
          value={street || ""}
          cityValue={{
            city: city || "",
            state: state || "",
            county: county || "",
            municipality: municipality || "",
          }}
          setValue={(value: string) => {
            setFormStateValue({
              delivery_attributes: {
                ...(formState.values.delivery_attributes || {}),
                street: value,
                postal_code: undefined,
              },
            })
          }}
        />

        <Field
          required
          label="Numer domu"
          name="house_number"
          inputProps={{
            value: house_number || "",
            onChange: (e) => {
              setFormStateValue({
                delivery_attributes: {
                  ...(formState.values.delivery_attributes || {}),
                  [e.currentTarget.name]: e.currentTarget.value,
                  postal_code: undefined,
                },
              })
            },
          }}
        />

        <Field
          label="Numer mieszkania"
          name="flat_number"
          inputProps={{
            value: formState.values.delivery_attributes?.flat_number || "",
            onChange: onInputChange,
          }}
        />

        <Field required label="Kod pocztowy" name="postal_code">
          <NumberFormat
            format="##-###"
            customInput={Input}
            name="postal_code"
            id="postal_code"
            value={postal_code || ""}
            onChange={onInputChange}
            size="large"
            selectOnFocus
            plain
          />
        </Field>
      </Card.Section>

      <Card.Section className={cls.submitBtns}>
        <Button
          className={cls.submitBtn}
          color="primary"
          variation="accented"
          size="extra-large"
          fullWidth
          onClick={setPreviousStep}
        >
          <Icon name="short-arrow-tiny-left" />
          Wstecz
        </Button>

        <Button className={cls.submitBtn} color="primary" size="extra-large" fullWidth onClick={submitStep}>
          Przejdź dalej
          <Icon name="short-arrow-tiny-right" />
        </Button>
      </Card.Section>
    </Card>
  )
}
